// BatchPdfDownloadDropdown.jsx — Per-Batch PDF Download (filtered by status)
// Drop-in dropdown button for batch rows and batch drawer toolbar.

import React, { useState, useEffect } from 'react';
import { Button, Tooltip, Dropdown, Tag, message, Modal } from 'antd';
import {
  CloudDownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const BatchPdfDownloadDropdown = ({ batchNo, size = 'small', buttonStyle = {}, compact = false }) => {
  const [counts, setCounts] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const fetchCounts = async () => {
    if (!batchNo) return;
    setLoadingCounts(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/${batchNo}/document-counts`);
      if (res.data.success) setCounts(res.data.counts);
    } catch (err) {
      console.error(`Failed to fetch counts for batch ${batchNo}:`, err);
    } finally {
      setLoadingCounts(false);
    }
  };

  useEffect(() => {
    if (dropdownOpen && !counts) fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOpen]);

  const executeDownload = async (status, count, statusLabel) => {
    setDownloading(status);
    const hideLoading = message.loading(
      `Downloading ${count} ${statusLabel} PDFs from Batch ${batchNo}...`,
      0
    );
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/procurement/batches/${batchNo}/download-pdfs?status=${status}`,
        { method: 'GET', headers: { 'Accept': 'application/zip' } }
      );
      if (!response.ok) {
        hideLoading();
        if (response.status === 404) {
          message.warning(`No ${statusLabel} documents in Batch ${batchNo}`);
        } else {
          const errorData = await response.json().catch(() => ({}));
          message.error(errorData.message || `Download failed (HTTP ${response.status})`);
        }
        setDownloading(null);
        return;
      }
      const blob = await response.blob();
      hideLoading();
      if (blob.size < 100) {
        message.error('Download failed — empty response');
        setDownloading(null);
        return;
      }
      let filename =
        response.headers.get('x-zip-filename') ||
        `Batch-${batchNo}-PDFs-${status}.zip`;
      const downloaded = response.headers.get('x-documents-downloaded') || '?';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 1000);
      const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);
      message.success(
        `Batch ${batchNo}: ${downloaded} PDFs downloaded (${sizeMB} MB)`,
        5
      );
    } catch (err) {
      hideLoading();
      console.error(`Batch ${batchNo} download error:`, err);
      message.error('Download failed. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownload = (status) => {
    const statusLabel =
      status === 'all' ? 'all' :
      status === 'pending_review' ? 'pending' :
      status;
    const count = counts?.[status] ?? 0;

    if (count === 0) {
      message.warning(`No ${statusLabel} documents in Batch ${batchNo}`);
      return;
    }

    if (count > 30) {
      Modal.confirm({
        title: `Download ${count} PDFs from Batch ${batchNo}?`,
        content: (
          <div style={{ fontSize: 13 }}>
            <p>
              <strong>{count}</strong> {statusLabel} PDFs from Batch{' '}
              <strong>{batchNo}</strong> will be packaged into a ZIP file.
            </p>
            <p style={{ color: '#8c8c8c' }}>
              Large downloads may take 1-2 minutes.
            </p>
          </div>
        ),
        okText: 'Download',
        onOk: () => executeDownload(status, count, statusLabel),
      });
    } else {
      executeDownload(status, count, statusLabel);
    }
  };

  const menuItems = [
    {
      key: 'info',
      label: (
        <div style={{
          padding: '4px 0', fontSize: 11, color: '#8c8c8c',
          borderBottom: '1px solid #f0f0f0', marginBottom: 4,
        }}>
          <FolderOpenOutlined style={{ marginRight: 6 }} />
          Batch {batchNo} — Download PDFs
        </div>
      ),
      disabled: true,
    },
    {
      key: 'approved',
      icon: downloading === 'approved'
        ? <LoadingOutlined spin style={{ color: '#52c41a' }} />
        : <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minWidth: 210 }}>
          <span style={{ fontWeight: 500 }}>Approved</span>
          <Tag color="success" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
            {loadingCounts ? '...' : (counts?.approved ?? '—')}
          </Tag>
        </div>
      ),
      disabled: downloading !== null || (counts?.approved || 0) === 0,
    },
    {
      key: 'rejected',
      icon: downloading === 'rejected'
        ? <LoadingOutlined spin style={{ color: '#ff4d4f' }} />
        : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minWidth: 210 }}>
          <span style={{ fontWeight: 500 }}>Rejected</span>
          <Tag color="error" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
            {loadingCounts ? '...' : (counts?.rejected ?? '—')}
          </Tag>
        </div>
      ),
      disabled: downloading !== null || (counts?.rejected || 0) === 0,
    },
    {
      key: 'pending_review',
      icon: downloading === 'pending_review'
        ? <LoadingOutlined spin style={{ color: '#faad14' }} />
        : <ClockCircleOutlined style={{ color: '#faad14' }} />,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minWidth: 210 }}>
          <span style={{ fontWeight: 500 }}>Pending</span>
          <Tag color="warning" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
            {loadingCounts ? '...' : (counts?.pending_review ?? '—')}
          </Tag>
        </div>
      ),
      disabled: downloading !== null || (counts?.pending_review || 0) === 0,
    },
    { type: 'divider' },
    {
      key: 'all',
      icon: downloading === 'all'
        ? <LoadingOutlined spin style={{ color: '#1a6ed8' }} />
        : <DownloadOutlined style={{ color: '#1a6ed8' }} />,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minWidth: 210 }}>
          <span style={{ fontWeight: 600, color: '#1a6ed8' }}>All PDFs</span>
          <Tag color="blue" style={{ borderRadius: 4, margin: 0, fontSize: 11 }}>
            {loadingCounts ? '...' : (counts?.all ?? '—')}
          </Tag>
        </div>
      ),
      disabled: downloading !== null || (counts?.all || 0) === 0,
    },
  ];

  const btn = (
    <Button
      size={size}
      icon={
        downloading ? <LoadingOutlined spin /> : <CloudDownloadOutlined />
      }
      style={{
        borderRadius: 3,
        fontSize: 11,
        color: downloading ? '#faad14' : '#1a5276',
        borderColor: downloading ? '#ffe58f' : '#91caff',
        ...buttonStyle,
      }}
    >
      {compact ? 'PDFs' : (downloading ? 'Downloading...' : 'Download PDFs')}
    </Button>
  );

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: ({ key }) => {
          if (key !== 'info') handleDownload(key);
        },
      }}
      trigger={['click']}
      placement="bottomRight"
      onOpenChange={setDropdownOpen}
    >
      <Tooltip title={`Download PDFs from Batch ${batchNo} by status`}>
        {btn}
      </Tooltip>
    </Dropdown>
  );
};

export default BatchPdfDownloadDropdown;